/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import HotelStrategyStrategyDetailWithTabsBaseController from 'lumen/controllers/hotel/strategy/strategy-detail-with-tabs-base';
import {
  COLUMN_PROPERTIES,
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_LOS,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatTableRate } from 'lumen/utils/strategy/detail-view-helpers';
import { timeout } from 'lumen/utils/waiters';

export default class HotelStrategyLosController extends HotelStrategyStrategyDetailWithTabsBaseController {
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service simpleExport;

  @tracked entity = SUBTYPE_VALUES_KEY_STRATEGY;

  typeId = TYPE_ID_LOS;

  exportRSTableTask = task({ drop: true }, async () => {
    const sheetName =
      this.typesDataPerId[this.typeId].formattedTypeName.toString();

    const headers = [this.intl.t('general.hotels').toString()];

    this.subtypes.forEach((subtype) => {
      // Add a label between brackets since grouped columns are not possible in the simple exports
      const subtypeLabel = subtype.formattedLabel[SUBTYPE_VALUES_KEY_STRATEGY];
      headers.push(`${this.intl.t('rates.rate')} (${subtypeLabel})`);
      headers.push(
        `${this.intl.t('strategy.metric_block.discount')} (${subtypeLabel})`,
      );
      headers.push(
        `${this.intl.t('reports.frequency_weekdays_short')} (${subtypeLabel})`,
      );
      headers.push(
        `${this.intl.t('reports.frequency_weekends_short')} (${subtypeLabel})`,
      );
    });

    const content = [];
    this.losDataPerHotelInfo.forEach((rowObject) => {
      const row = [rowObject[COLUMN_PROPERTIES.HOTELINFO].formattedValue];

      this.subtypes.forEach((subtype) => {
        row.push(
          formatTableRate(rowObject[subtype.id][COLUMN_PROPERTIES.RATE]),
        );
        row.push(
          rowObject[subtype.id][COLUMN_PROPERTIES.DISCOUNT].formattedValue,
        );
        row.push(
          rowObject[subtype.id][COLUMN_PROPERTIES.FREQUENCY_WEEKDAY]
            .formattedValue,
        );
        row.push(
          rowObject[subtype.id][COLUMN_PROPERTIES.FREQUENCY_WEEKEND]
            .formattedValue,
        );
      });

      content.push(row);
    });

    const postData = JSON.stringify({ headers, content, sheetName });
    this.simpleExport.execute(postData, this.hotelAndMarketService.hotel?.id);

    // Add a cooldown before the export can be triggered again
    // Prevents multiple downloads on double click or worse: Chrome disabling downloads from mylighthouse.com
    await timeout();
  });

  @computed('hasRSData', 'selectedCompetitors', 'subtypes', 'typesDataPerId')
  get losDataPerHotelInfo() {
    const losDataPerHotelInfo = new Map();

    if (this.hasRSData) {
      // Initialize Map
      this.selectedCompetitors.forEach((hotelinfo) => {
        const tableColumns = {
          [COLUMN_PROPERTIES.HOTELINFO]: {
            id: hotelinfo.id,
            formattedValue: hotelinfo.name,
            isOwnHotelInfo:
              this.hotelAndMarketService.isOwnHotelInfo(hotelinfo),
          },
        };

        // Add a dummy object for each subtype (corresponds to a grouped column in the detail view)
        this.subtypes.forEach((subtype) => {
          tableColumns[subtype.id] = {};
        });

        losDataPerHotelInfo.set(hotelinfo.id, tableColumns);
      });

      this.subtypes.forEach((subtype) => {
        subtype.filteredStrategies.forEach((strategy) => {
          losDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.RATE
          ] = {
            value: strategy.discountedPrice,
            formattedValue: strategy.formattedDiscountedPrice,
            baserate: strategy.rsbaserate,
            hasNoData: strategy.hasNoData,
            subsortOrder: strategy.subsortOrder,
            isNonComputable: strategy.isNonComputable,
          };
          losDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.DISCOUNT
          ] = {
            value: strategy.supplementOrDiscount,
            formattedValue: strategy.formattedValue,
            hasNoData: strategy.hasNoData,
            isNonComputable: strategy.isNonComputable,
            subsortOrder: strategy.subsortOrder,
          };
          losDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.FREQUENCY_WEEKDAY
          ] = {
            value: strategy.frequencyWeekday,
            formattedValue: strategy.formattedFrequencyWeekday,
            hasNoData: strategy.hasNoData,
            subsortOrder: strategy.subsortOrder,
          };
          losDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            [COLUMN_PROPERTIES.FREQUENCY_WEEKEND]
          ] = {
            value: strategy.frequencyWeekend,
            formattedValue: strategy.formattedFrequencyWeekend,
            hasNoData: strategy.hasNoData,
            subsortOrder: strategy.subsortOrder,
          };
        });
      });
    }

    return losDataPerHotelInfo;
  }
}
